import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Container } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router";
import ProjectSidebar from "./ProjectSidebar";
import ProjectContent from "./ProjectContent";
import AccessDeniedPage from "../pages/AccessDeniedPage";
import {
  Windpark,
  PageType,
  PermitProcedure,
  ProcedureTransition,
} from "../../types";
import { useSeitenRechte } from "../../auth/hooks/useSeitenRechte";
import {
  ProjektRechteProvider,
  useProjektRechte,
} from "../../contexts/ProjektRechteContext";
import {
  PageWarningsProvider,
  usePageWarnings,
} from "../../contexts/PageWarningsContext";
import { projectsApi } from "../../services/api/projects";
import { permitsApi } from "../../services/api/permits";
import { antragKurztitel } from "../../utils/antragKurztitel";
import { SeitenTitelContext, SeitenTitelDaten } from "./SeitenTitelContext";
import { SIDEBAR_WIDTH } from "./Sidebar";
import { SEITE_ZU_BEREICH } from "../../utils/zugriff";
import {
  useHeader,
  useHeaderShareProject,
} from "../../contexts/HeaderContext";
import { useToast } from "../../contexts/ToastContext";
import { parseApiError } from "../../services/api/errorPresentation";
import PageHeader from "./PageHeader";
import SeitenTitel from "./SeitenTitel";
import ProcedurePageHeader, {
  ProcedurePage,
} from "../pages/projectPermits/sections/ProcedurePageHeader";

export { SIDEBAR_WIDTH };

// Context für den Edit-Modus
interface EditModeContextType {
  isEditMode: boolean;
}

const EditModeContext = createContext<EditModeContextType | undefined>(
  undefined,
);

// Hook für den Edit-Modus
export const useEditMode = () => {
  const context = useContext(EditModeContext);
  if (context === undefined) {
    throw new Error("useEditMode must be used within a ProjectLayout");
  }
  return context;
};

/**
 * Die Anträge stehen im Seitenkopf (Auswahl, Status, nächster Schritt). Geladen werden sie hier
 * einmal; die Seiten des Moduls Genehmigung melden ihren Stand zurück, sobald sie einen Antrag
 * anlegen, ändern oder löschen – so folgt der Kopf ohne zweiten Abruf.
 */
interface AntragNavigationContextType {
  meldeAntraege: (procedures: PermitProcedure[]) => void;
  /**
   * The procedure last changed by a transition in the page header – pages holding their own
   * copy take it over (and reload what the transition changes with it, e.g. deadlines).
   */
  changedProcedure: PermitProcedure | null;
  /** Führt zu einer Seite eines Antrags bzw. ohne ID zur Liste aller Anträge */
  oeffneAntrag: (
    procedureId: string | null,
    seite?:
      | "project-permit-elia"
      | "project-permit-evidences"
      | "project-permit-costs",
    optionen?: {
      replace?: boolean;
      /** Öffnet auf der Seite "Nachweise" gleich diesen Nachweis (`?nachweis=`) */
      nachweisId?: string;
    },
  ) => void;
}

const AntragNavigationContext = createContext<
  AntragNavigationContextType | undefined
>(undefined);

export const useAntragNavigation = () => {
  const context = useContext(AntragNavigationContext);
  if (context === undefined) {
    throw new Error("useAntragNavigation must be used within a ProjectLayout");
  }
  return context;
};

/** Platzhalter für die Antrags-ID in den Pfaden der Antragsseiten */
const ANTRAG_PLATZHALTER = ":procedureId";

/** Eine Seite des Projekts: Route, Tab-Index in der Sidebar und Anzeigename */
interface ProjectPage {
  page: PageType;
  /** Pfad unterhalb von /project/{projectId}; Antragsseiten mit `:procedureId` */
  path: string;
  index: number;
  /**
   * Name für Meldungen außerhalb der Sidebar. Seiten in Modulen mit mehreren Seiten führen den
   * Modulnamen mit – ohne ihn wäre "Kosten" nicht von "Kosten" zu unterscheiden, das Modul
   * Genehmigung und das Modul Kalkulation haben je eine.
   */
  label: string;
}

/**
 * Alle Seiten des Projekts an einer Stelle.
 *
 * Route, Tab-Index und Name lagen früher in drei getrennten switch-Blöcken und mussten von
 * Hand synchron gehalten werden. Die Indizes entsprechen denen in `ProjectSidebar`; neue
 * Seiten hängen sich hinten an, damit bestehende unberührt bleiben.
 */
const PROJECT_PAGES: ProjectPage[] = [
  { page: "project-overview", path: "overview", index: 0, label: "Übersicht" },
  { page: "project-company", path: "company", index: 1, label: "Gesellschaft" },
  {
    page: "project-permit-applications",
    path: "permits",
    index: 2,
    label: "Genehmigung › Anträge",
  },
  {
    page: "project-area",
    path: "area",
    index: 3,
    label: "Fläche › Flächensicherung",
  },
  {
    page: "project-generation-units",
    path: "generation-units",
    index: 4,
    label: "Fläche › Anlagen",
  },
  { page: "project-yield", path: "yield", index: 5, label: "Fläche › Erträge" },
  {
    page: "project-revenues",
    path: "revenues",
    index: 6,
    label: "Kalkulation › Erlöse",
  },
  {
    page: "project-costs",
    path: "costs",
    index: 7,
    label: "Kalkulation › Kosten",
  },
  {
    page: "project-financing",
    path: "financing",
    index: 8,
    label: "Kalkulation › Finanzierung",
  },
  {
    page: "project-calculation",
    path: "calculation",
    index: 9,
    label: "Kalkulation › Wirtschaftlichkeit",
  },
  {
    page: "project-permit-evidences",
    path: `permits/${ANTRAG_PLATZHALTER}/evidences`,
    index: 10,
    label: "Genehmigung › Nachweise",
  },
  {
    page: "project-permit-costs",
    path: `permits/${ANTRAG_PLATZHALTER}/costs`,
    index: 11,
    label: "Genehmigung › Kosten",
  },
  {
    page: "project-permit-elia",
    path: `permits/${ANTRAG_PLATZHALTER}/elia`,
    index: 12,
    label: "Genehmigung › Antrag",
  },
];

/** Überschrift einer Seite aus ihrem Namen: von „Modul › Seite" die Seite */
const seitenTitelAus = (seite: ProjectPage): SeitenTitelDaten => {
  const teile = seite.label.split(" › ");
  return { titel: teile[teile.length - 1] };
};

const PAGE_BY_TYPE = Object.fromEntries(
  PROJECT_PAGES.map((page) => [page.page, page]),
) as Record<PageType, ProjectPage>;

/** Liste aller Anträge – zugleich der Anfang aller Routen des Moduls Genehmigung */
const PERMITS_PATH = "permits";

/**
 * Bis die Anträge eigene Seiten bekamen, lagen Nachweise und Kosten projektweit unter diesen
 * Pfaden. Bestehende Links und Lesezeichen führen zur Liste der Anträge.
 */
const LEGACY_PERMITS_PATHS = [
  "permits/applications",
  "permits/evidences",
  "permits/costs",
];

/** Der Teil hinter /project/{projectId} – die Route der Seite */
const getPagePath = (pathname: string): string =>
  pathname.split("/").filter(Boolean).slice(2).join("/");

/**
 * Vergleicht einen Pfad mit dem Muster einer Seite, Segment für Segment. Liefert bei Treffer
 * die Antrags-ID (bzw. `null` bei Seiten ohne Antrag), sonst `undefined`.
 */
const passtAufMuster = (
  muster: string,
  pfad: string,
): string | null | undefined => {
  const musterTeile = muster.split("/");
  const pfadTeile = pfad.split("/");
  if (musterTeile.length !== pfadTeile.length) return undefined;
  let procedureId: string | null = null;
  for (let i = 0; i < musterTeile.length; i++) {
    if (musterTeile[i] === ANTRAG_PLATZHALTER) procedureId = pfadTeile[i];
    else if (musterTeile[i] !== pfadTeile[i]) return undefined;
  }
  return procedureId;
};

/** Pfad einer Seite unterhalb von /project/{projectId}, bei Antragsseiten mit der Antrags-ID */
const pfadDerSeite = (seite: ProjectPage, procedureId?: string | null) =>
  seite.path.replace(ANTRAG_PLATZHALTER, procedureId ?? "");

/**
 * Leitet Seite und Antrag aus der URL ab. Verglichen wird der vollständige Pfad, nicht ein
 * Teilstring: "permits/…/costs" enthält "costs", ist aber nicht die Kalkulations-Kostenseite.
 */
const getCurrentPageFromUrl = (
  pathname: string,
): { page: PageType; procedureId: string | null } => {
  const pfad = getPagePath(pathname);
  if (LEGACY_PERMITS_PATHS.includes(pfad)) {
    return { page: "project-permit-applications", procedureId: null };
  }
  for (const seite of PROJECT_PAGES) {
    const procedureId = passtAufMuster(seite.path, pfad);
    if (procedureId !== undefined) return { page: seite.page, procedureId };
  }
  return { page: "project-overview", procedureId: null };
};

interface ProjectLayoutProps {
  projectName: string;
  currentProject: Windpark;
  onBack?: () => void;
  /** Callback wenn Projekt-Daten aktualisiert wurden (z.B. aktiver Ertragswert) */
  onProjectUpdated?: () => void;
}

/**
 * Innere Layout-Komponente (innerhalb des PageWarningsProvider)
 * Nutzt usePageWarnings um Warnings an die Sidebar weiterzugeben
 */
const ProjectLayoutInner: React.FC<ProjectLayoutProps> = ({
  projectName,
  currentProject,
  onBack,
  onProjectUpdated,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Page-Warnings aus dem Context lesen und setzen
  const { hasWarning, setValidationsFromAPI } = usePageWarnings();

  // Validierungen beim Projekt-Load abrufen
  // Wird bei Mount und bei Änderung der projectId ausgeführt
  useEffect(() => {
    const loadValidations = async () => {
      if (!currentProject?.id) return;

      try {
        const validations = await projectsApi.getProjectValidations(
          currentProject.id,
        );
        setValidationsFromAPI(validations);
      } catch (err) {
        // Fehler ignorieren - Validierungen sind nicht kritisch für die Seitendarstellung
      }
    };

    loadValidations();
  }, [currentProject?.id, setValidationsFromAPI]);

  const { page: currentPage, procedureId: currentProcedureId } =
    getCurrentPageFromUrl(location.pathname);

  // Frühere Pfade der Genehmigung führen zur Liste der Anträge – bestehende Links und
  // Lesezeichen sollen weiter funktionieren.
  useEffect(() => {
    if (LEGACY_PERMITS_PATHS.includes(getPagePath(location.pathname))) {
      navigate(`/project/${projectId}/${PERMITS_PATH}`, { replace: true });
    }
  }, [location.pathname, projectId, navigate]);

  // Anträge für den Seitenkopf – nur, wer die Genehmigung sehen darf; sonst antwortete der
  // Server ohnehin mit 403.
  const genehmigungsRechte = useSeitenRechte("project-permit-applications");
  const darfAntraegeSehen =
    genehmigungsRechte.geladen && genehmigungsRechte.canView;
  const [antraege, setAntraege] = useState<PermitProcedure[]>([]);

  useEffect(() => {
    if (!darfAntraegeSehen) return;
    let abgebrochen = false;
    permitsApi
      .getPermitProcedures(currentProject.id)
      .then((procedures) => {
        if (!abgebrochen) setAntraege(procedures ?? []);
      })
      .catch(() => {
        // Ohne Anträge bleibt der Kopf eines Antrags leer – die Seite selbst meldet Fehler.
      });
    return () => {
      abgebrochen = true;
    };
  }, [currentProject.id, darfAntraegeSehen]);

  const oeffneAntrag = useCallback<AntragNavigationContextType["oeffneAntrag"]>(
    (procedureId, seite = "project-permit-elia", optionen) => {
      const pfad = procedureId
        ? pfadDerSeite(PAGE_BY_TYPE[seite], procedureId)
        : PERMITS_PATH;
      const suche = optionen?.nachweisId
        ? `?nachweis=${encodeURIComponent(optionen.nachweisId)}`
        : "";
      navigate(`/project/${projectId}/${pfad}${suche}`, {
        replace: optionen?.replace,
      });
    },
    [navigate, projectId],
  );

  const kurztitel = useMemo(() => antragKurztitel(antraege), [antraege]);

  const [changedProcedure, setChangedProcedure] =
    useState<PermitProcedure | null>(null);
  const { showError, showSuccess } = useToast();

  /** A transition from the page header: the head updates itself, the page follows via context */
  const transitionAntrag = useCallback(
    async (
      procedureId: string,
      data: ProcedureTransition,
    ): Promise<PermitProcedure | null> => {
      try {
        const updated = await permitsApi.transitionPermitProcedure(
          currentProject.id,
          procedureId,
          data,
        );
        setAntraege((prev) =>
          prev.map((p) => (p.id === procedureId ? updated : p)),
        );
        setChangedProcedure(updated);
        showSuccess("Gespeichert");
        return updated;
      } catch (err) {
        showError(parseApiError(err).message);
        return null;
      }
    },
    [currentProject.id, showError, showSuccess],
  );

  const antragNavigation = useMemo<AntragNavigationContextType>(
    () => ({
      meldeAntraege: setAntraege,
      changedProcedure,
      oeffneAntrag,
    }),
    [changedProcedure, oeffneAntrag],
  );

  const seitenTitel = useMemo(
    () => seitenTitelAus(PAGE_BY_TYPE[currentPage]),
    [currentPage],
  );

  // Rechte des Projekts (Auth0-Scopes für Interne, Freigaben für Externe)
  const currentPermissions = useSeitenRechte(currentPage);
  const { darfSehen, geladen: rechteGeladen, istIntern } = useProjektRechte();

  // „Projekt teilen“ im Einstellungsmenü gibt es nur für Mitglieder des Mandanten: Ein
  // Externer soll weder erfahren, wer sonst noch Zugriff hat, noch selbst weitergeben können.
  // Was der Dialog ändert, prüft ohnehin der Server. Erst nach dem Laden der Rechte – bis
  // dahin gilt jeder als intern, und ein Gast bekäme die Zugriffsliste mit 403 abgewiesen.
  useHeaderShareProject(
    rechteGeladen && istIntern
      ? { id: currentProject.id, name: projectName }
      : null,
  );

  // Solange die Rechte laden, bleibt die Navigation vollständig – sonst würde sie sichtbar
  // zusammenklappen und sich gleich wieder füllen.
  const istSichtbar = (key: string) =>
    !rechteGeladen || darfSehen(SEITE_ZU_BEREICH[key]);

  // isEditMode wird automatisch durch canEdit der aktuellen Seite gesetzt
  const isEditMode = currentPermissions.canEdit;

  const currentTab = PAGE_BY_TYPE[currentPage].index;
  // The pages of a procedure are its tabs; in the sidebar "Anträge" stays marked.
  const sidebarTab = currentProcedureId
    ? PAGE_BY_TYPE["project-permit-applications"].index
    : currentTab;

  // Who may change the procedure (Antrag page right) may trigger its next step from the head,
  // on every tab.
  const antragRechte = useSeitenRechte("project-permit-elia");
  const { pageHeading } = useHeader();
  const currentProcedure = currentProcedureId
    ? antraege.find((antrag) => antrag.id === currentProcedureId)
    : undefined;
  // An opened record (e.g. a Nachweis) brings its way back; it stands below the head.
  const recordHeading = pageHeading?.back ? (
    <SeitenTitel back={pageHeading.back} titel={pageHeading.titel} />
  ) : undefined;

  const pageHeader = currentProcedure ? (
    <ProcedurePageHeader
      procedure={currentProcedure}
      procedures={antraege}
      shortTitles={kurztitel}
      activePage={currentPage as ProcedurePage}
      onOpen={(procedureId, seite) => oeffneAntrag(procedureId, seite)}
      onTransition={transitionAntrag}
      editable={antragRechte.canEdit}
      isVisible={istSichtbar}
      hasWarning={hasWarning}
      below={recordHeading}
    />
  ) : (
    <PageHeader title={seitenTitel.titel} below={recordHeading} />
  );

  const handleTabChange = (tabIndex: number) => {
    const ziel =
      PROJECT_PAGES.find((page) => page.index === tabIndex) ??
      PAGE_BY_TYPE["project-overview"];
    navigate(`/project/${projectId}/${pfadDerSeite(ziel, currentProcedureId)}`);
  };

  const editModeValue: EditModeContextType = {
    isEditMode,
  };

  // Zentrale Berechtigungsprüfung - prüfe ob User die aktuelle Seite sehen darf
  if (!currentPermissions.canView) {
    return (
      <EditModeContext.Provider value={editModeValue}>
        <AntragNavigationContext.Provider value={antragNavigation}>
          <Box sx={{ display: "flex" }}>
            {/* Sidebar Navigation */}
            <ProjectSidebar
              projectName={projectName}
              currentTab={sidebarTab}
              onTabChange={handleTabChange}
              hasWarning={hasWarning}
              istSichtbar={istSichtbar}
            />

            {/* AccessDeniedPage wenn keine Berechtigung */}
            <Box
              sx={{
                flex: 1,
                marginLeft: `${SIDEBAR_WIDTH}px`,
                width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
                maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
                overflow: "hidden",
              }}
            >
              <AccessDeniedPage
                bereichName={PAGE_BY_TYPE[currentPage].label}
                pageName={PAGE_BY_TYPE[currentPage].label}
                onBack={onBack || (() => {})}
              />
            </Box>
          </Box>
        </AntragNavigationContext.Provider>
      </EditModeContext.Provider>
    );
  }

  return (
    <EditModeContext.Provider value={editModeValue}>
      <AntragNavigationContext.Provider value={antragNavigation}>
        <Box sx={{ display: "flex" }}>
          {/* Sidebar Navigation */}
          <ProjectSidebar
            projectName={projectName}
            currentTab={sidebarTab}
            onTabChange={handleTabChange}
            hasWarning={hasWarning}
            istSichtbar={istSichtbar}
          />

          {/* Hauptinhalt - eingerückt um Sidebar-Breite */}
          <Box
            sx={{
              flex: 1,
              marginLeft: `${SIDEBAR_WIDTH}px`,
              width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
              maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
            }}
          >
            <SeitenTitelContext.Provider value={seitenTitel}>
              <Container maxWidth={false}>{pageHeader}</Container>
              <ProjectContent
                currentPage={currentPage}
                procedureId={currentProcedureId}
                currentProject={currentProject}
                canView={currentPermissions.canView}
                canEdit={currentPermissions.canEdit}
                onProjectUpdated={onProjectUpdated}
              />
            </SeitenTitelContext.Provider>
          </Box>
        </Box>
      </AntragNavigationContext.Provider>
    </EditModeContext.Provider>
  );
};

/**
 * Project Layout Component
 * Koordiniert Sidebar und Hauptinhalt für die Projekt-Ansicht
 * Wraps ProjectLayoutInner mit PageWarningsProvider für Warning-Management
 */
const ProjectLayout: React.FC<ProjectLayoutProps> = (props) => {
  return (
    <PageWarningsProvider>
      <ProjektRechteProvider projectId={props.currentProject.id}>
        <ProjectLayoutInner {...props} />
      </ProjektRechteProvider>
    </PageWarningsProvider>
  );
};

export default ProjectLayout;
